import { z } from "zod";

/**
 * Conditional logic, per docs/01-PRODUCT-SPEC.md §3.3:
 *
 *   WHEN <all|any> of [ {field, operator, value} ]
 *   THEN [ {action, target} ]
 *
 * Rules live on the form version, are evaluated on the client for responsiveness, and are
 * **re-evaluated on the server at submit** — a hidden field's value is discarded server-side.
 */

export const LOGIC_OPERATORS = [
  "equals",
  "not_equals",
  "contains",
  "greater_than",
  "less_than",
  "is_empty",
  "is_not_empty",
  "is_one_of",
] as const;

export const LOGIC_ACTIONS = [
  "show",
  "hide",
  "require",
  "unrequire",
  "set_value",
  "jump_to_page",
  "skip_page",
] as const;

export const LOGIC_TARGET_TYPES = ["field", "section", "page"] as const;

export type LogicOperator = (typeof LOGIC_OPERATORS)[number];
export type LogicAction = (typeof LOGIC_ACTIONS)[number];
export type LogicTargetType = (typeof LOGIC_TARGET_TYPES)[number];

export const logicConditionSchema = z.object({
  field: z.string().min(1),
  operator: z.enum(LOGIC_OPERATORS),
  // Not required for is_empty / is_not_empty; an array for is_one_of.
  value: z.unknown().optional(),
});

export const logicEffectSchema = z.object({
  action: z.enum(LOGIC_ACTIONS),
  targetType: z.enum(LOGIC_TARGET_TYPES),
  /** A field key, a section key, or a page number rendered as a string. */
  target: z.string().min(1),
  /** Only meaningful for `set_value`. */
  value: z.unknown().optional(),
});

export const logicRuleSchema = z.object({
  id: z.string().min(1),
  match: z.enum(["all", "any"]).default("all"),
  conditions: z.array(logicConditionSchema).min(1),
  effects: z.array(logicEffectSchema).min(1),
});

export type LogicCondition = z.infer<typeof logicConditionSchema>;
export type LogicEffect = z.infer<typeof logicEffectSchema>;
export type LogicRule = z.infer<typeof logicRuleSchema>;

export const logicRulesSchema = z.array(logicRuleSchema);

/** What the evaluator returns — consumed by both the renderer and the submit path. */
export interface LogicEvaluationResult {
  hiddenFieldKeys: string[];
  hiddenSectionKeys: string[];
  requiredOverrides: Record<string, boolean>;
  skippedPages: number[];
  computedValues: Record<string, unknown>;
  /** Populated when circular references are detected; those rules are not applied. */
  circularRuleIds: string[];
}
